import { NlpTemplate, Page } from '@testsigmainc/models';
import {
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from 'react-query';
import api from '../../../api/instance';
import { NlpTemplateExample } from '@modules/test-development/models/nlp-template-example.model';
import { PrivateURLConstants } from '@constants/private-url.constant';
const nameSpace = PrivateURLConstants.NLP_TEMPLATE;
const DEFAULT_PAGE_SIZE = 2000;

interface UseFindAllArgs
  extends Omit<
    UseQueryOptions<NlpTemplate[], Error>,
    'queryKey' | 'queryFn'
  > {
  additionalQueryKey?: QueryKey | string | number | boolean | null | undefined;
}

let fetchAllNlpTemplatesPromise: Promise<NlpTemplate[]> | null = null;

const fetchAllNlpTemplates = async (): Promise<NlpTemplate[]> => {
  if (fetchAllNlpTemplatesPromise) {
    return fetchAllNlpTemplatesPromise;
  }

  fetchAllNlpTemplatesPromise = (async () => {
    const templates: NlpTemplate[] = [];
    let pageNumber = 0;
    let hasNextPage = true;

    while (hasNextPage) {
      const response = await api
        .get(nameSpace, { page: pageNumber, size: DEFAULT_PAGE_SIZE })
        .then((res) =>
          new Page<NlpTemplate>().deserialize(res, NlpTemplate as any),
        );

      templates.push(...response.content);

      const totalPages = response.totalPages ?? Number.MAX_SAFE_INTEGER;
      const reachedLastPage =
        response.last ||
        response.content.length === 0 ||
        pageNumber + 1 >= totalPages;

      hasNextPage = !reachedLastPage;
      pageNumber += 1;
    }

    return templates;
  })();

  try {
    return await fetchAllNlpTemplatesPromise;
  } finally {
    fetchAllNlpTemplatesPromise = null;
  }
};

const findAll = (query?): Promise<Page<NlpTemplate>> => {
  return new Promise((resolve, reject) => {
    api
      .get(nameSpace, { query })
      .then((res) =>
        resolve(new Page<NlpTemplate>().deserialize(res, NlpTemplate as any)),
      )
      .catch((e) => reject(e));
  });
};

const findExample = (templateId: number) => {
  return api
    .get(`${nameSpace}/${templateId}/example`)
    .then((res) => new NlpTemplateExample().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const findAllWithoutPagination = (): Promise<NlpTemplate[]> => {
  return fetchAllNlpTemplates();
};

const useFindAll = ({
  additionalQueryKey = [],
  ...options
}: UseFindAllArgs = {}): UseQueryResult<NlpTemplate[], Error> => {
  const baseKey: QueryKey = ['findAllNlpTemplates'];
  const normalizedAdditionalKey = Array.isArray(additionalQueryKey)
    ? additionalQueryKey
    : [additionalQueryKey];

  const queryKey: QueryKey = [...baseKey, ...normalizedAdditionalKey];

  return useQuery<NlpTemplate[], Error>({
    queryKey,
    queryFn: () => fetchAllNlpTemplates(),
    staleTime: Infinity,
    cacheTime: Infinity,
    ...options,
  });
};

const NlpTemplateService = {
  findAll,
  findAllWithoutPagination,
  useFindAll,
  findExample,
};

export default NlpTemplateService;
